import type { Notification, NotificationType } from '../../types/notification';
import { notificationStorage } from './notificationStorage';
import { store } from '../../app/store';
import { addNotification } from '../../app/store/slices/notificationSlice';
import { useUserStore } from '../../app/store/useUserStore';

class NotificationService {
  /**
   * Add a generic notification
   */
  private addNotificationInternal(
    title: string,
    message: string,
    type: NotificationType,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    // Get current user info from store if not provided
    const currentUser = useUserStore.getState().user;
    const currentUserRole = options?.user_role || currentUser?.user_role;
    const currentUserId = options?.userId || currentUser?.uuid || String(currentUser?.user_id || '');

    const notification: Omit<Notification, 'id' | 'timestamp'> = {
      title,
      message,
      type,
      isRead: false,
      userId: currentUserId || undefined,
      user_role: currentUserRole as 'traveller' | 'sender' | undefined,
      relatedEntityId: options?.relatedEntityId,
      data: options?.data,
    };

    // Add to localStorage via storage utility
    const result = notificationStorage.addNotification(notification);

    // Update Redux state
    if (result) {
      store.dispatch(addNotification(notification));
    }

    return result;
  }

  /**
   * Add a success notification
   */
  addSuccessNotification(
    title: string,
    message: string,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, 'success', options);
  }

  /**
   * Add an error notification
   */
  addErrorNotification(
    title: string,
    message: string,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, 'error', options);
  }

  /**
   * Add a warning notification
   */
  addWarningNotification(
    title: string,
    message: string,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, 'warning', options);
  }

  /**
   * Add an info notification
   */
  addInfoNotification(
    title: string,
    message: string,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, 'info', options);
  }

  /**
   * Add a system notification
   */
  addSystemNotification(
    title: string,
    message: string,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, 'system', options);
  }

  /**
   * Add a user-friendly notification (defaults to info)
   */
  addUserNotification(
    title: string,
    message: string,
    type: NotificationType = 'info',
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, type, options);
  }

  /**
   * Add a shipment offer request notification
   */
  addShipmentOfferNotification(
    title: string,
    message: string,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, 'shipment_offer_request', options);
  }

  /**
   * Add an order/item notification
   */
  addOrderNotification(
    title: string,
    message: string,
    options?: {
      userId?: string;
      relatedEntityId?: string;
      data?: Record<string, unknown>;
      user_role?: 'traveller' | 'sender';
    }
  ): Notification | null {
    return this.addNotificationInternal(title, message, 'order', options);
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
